import { IFilterBarData } from '@/components/basicComponents/filterBar';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { Loader } from '@/components/loaders/loader';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { ISort, SORT_ORDER } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { allStrategiesTableMobileSort } from '../../constants';
import { LIST_INVESTMENTS_FOR_MANAGER_UPDATE_QUERY, MANAGER_UPDATES_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY } from '../../queries';
import { Table } from './table/table';
import MobileTable from './table/tableMobile';
import { ManagerUpdatesInvestmentWorkflowStateFiltersQuery, ManagerUpdatesPageListInvestmentsForManagerUpdateQuery } from '@/__generated__/graphql';
import { SECURITY_STATUS } from '@trace-8/trace8-schema';

const quickFilterOptions = [
  createSimpleSelectOption('', 'All'),
  createSimpleSelectOption('Strategies with Updates'),
  createSimpleSelectOption('Strategies without Updates'),
  createSimpleSelectOption('Strategies with New AI Suggestions')
];

export const AllStrategiesTab = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();

  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const q = searchParams.get('q') || '';
  const key = searchParams.get('key') || 'STRATEGY';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const filterBarParams = searchParams.get('filterBar');
  const primaryFilter = searchParams.get('primaryFilter') || '';
  const position = searchParams.get('position') || 0;

  const [managerUpdatesRows, setManagerUpdatesRows] = useState<
    ManagerUpdatesPageListInvestmentsForManagerUpdateQuery['listInvestmentsForManagerUpdate']['data']
  >([]);
  const [statuses, setStatuses] = useState<ManagerUpdatesInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters']>([]);
  const [filters, setFilters] = useState<IFilterBarData[]>([]);
  const [filterBar, setFilterBar] = useState<IFilterBarData[]>([]);
  const [mobileSort, setMobileSort] = useState(allStrategiesTableMobileSort[0].title);
  const sortItem = allStrategiesTableMobileSort.find((item) => item.title === mobileSort);
  const mobileSortList = allStrategiesTableMobileSort.map((item) => item.title);

  // Initialize filterBar when statuses are loaded
  useEffect(() => {
    if (!statuses.length) return;

    const initiallyEnabledFilters = [SECURITY_STATUS.OPEN, SECURITY_STATUS.ACTIVE];

    const initialFilterBar = statuses.map((item) => {
      let enabled = initiallyEnabledFilters.includes(item.subjectStatus as SECURITY_STATUS);
      if (filterBarParams) {
        const parsedParams = JSON.parse(filterBarParams);
        enabled = parsedParams.includes(item.name);
      }

      const existingItem = filterBar.find((existing) => existing.value === item.name);

      return {
        name: item.label,
        type: 'statusFilter',
        value: item.name,
        enabled,
        count: existingItem?.count || 0
      };
    });

    setFilterBar(initialFilterBar);
  }, [statuses, filterBarParams]);

  // Update counts only after query completes
  useEffect(() => {
    if (!filters.length || !filterBar.length) return;

    const updatedFilterBar = filterBar.map((item) => {
      const matchedFilter = filters.find((filter) => filter.value === item.value);
      return {
        ...item,
        count: matchedFilter?.count ?? item.count
      };
    });

    const countsChanged = updatedFilterBar.some((item, index) => item.count !== filterBar[index].count);

    if (countsChanged) {
      setFilterBar(updatedFilterBar);
    }
  }, [filters]);

  const { loading: investmentWorkflowStateFiltersLoading } = useQuery<{
    InvestmentWorkflowStateFilters: ManagerUpdatesInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  }>(MANAGER_UPDATES_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, {
    onCompleted: (data) => {
      setStatuses(data.InvestmentWorkflowStateFilters);
    }
  });

  const {
    data: listInvestmentsData,
    refetch: listInvestmentsRefetch,
    loading: listInvestmentsLoading
  } = useQuery(LIST_INVESTMENTS_FOR_MANAGER_UPDATE_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !statuses.length || !filterBar.length,
    variables: {
      sortBy: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      managerUpdateFilter: filterBar.filter((item) => item.enabled).map((item) => item.value) as string[],
      quickStatusFilter: primaryFilter
    },
    onCompleted: (data) => {
      const { filters, data: tableData } = data.listInvestmentsForManagerUpdate;

      setFilters(filters as IFilterBarData[]);
      setManagerUpdatesRows(tableData);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!isMobile) return;

    listInvestmentsRefetch({
      sortBy: sortItem?.key,
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1
    });
  }, [mobileSort]);

  const onChangeSearch = (searchValue: string) => {
    setSearchParams(
      (prev) => {
        if (!searchValue) {
          prev.delete('q');
          return prev;
        }
        prev.delete('limit');
        prev.delete('offset');
        prev.set('q', searchValue);
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePrimaryFilter = (filter: string) => {
    setSearchParams(
      (prev) => {
        !!filter ? prev.set('primaryFilter', filter) : prev.delete('primaryFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeFilterBar = (filterBarItems: IFilterBarData[]) => {
    setFilterBar(filterBarItems);
    const filterBarValues = filterBarItems.filter((item) => item.enabled).map((item) => item.value);
    setSearchParams(
      (prev) => {
        if (!filterBarValues.length) {
          prev.delete('filterBar');
          return prev;
        }
        prev.delete('limit');
        prev.delete('offset');
        prev.set('filterBar', JSON.stringify(filterBarValues));
        return prev;
      },
      { replace: true }
    );
  };

  const saveScrollPostion = () => {
    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const makeScroll = (position: string | number) => {
    if (!position) return;
    setTimeout(() => window.scrollTo({ top: Number(position), behavior: 'smooth' }), 0);
    setSearchParams(
      (prev) => {
        prev.delete('position');
        return prev;
      },
      { replace: true }
    );
  };

  const openManagerUpdateDetailsPage = ({ strategyId, documentRecordId }: { strategyId: string; documentRecordId?: string }) => {
    saveScrollPostion();
    const params = new URLSearchParams();
    if (documentRecordId) params.set('documentRecordId', documentRecordId);
    params.set('tab', 'Updates');
    params.set('backToTitle', 'Manager Updates');
    navigate(`/strategyDetails/${strategyId}?${params.toString()}`);
  };

  if (investmentWorkflowStateFiltersLoading) return <LoaderOnWholeScreen />;

  return (
    <>
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        quickFilter={{
          title: 'Showing',
          defaultValue: 'All',
          value: primaryFilter,
          onChange: onChangePrimaryFilter,
          options: quickFilterOptions
        }}
        mobileSort={{
          value: mobileSort,
          onChange: setMobileSort,
          selects: mobileSortList
        }}
        filterBar={filterBar}
        onChangeFilterBar={onChangeFilterBar}
        resultsValue={listInvestmentsData?.listInvestmentsForManagerUpdate?.total ?? 0}
        refetch={listInvestmentsRefetch}
        useNewFilters
      />
      {isMobile ? (
        <>
          {listInvestmentsLoading ? (
            <Loader />
          ) : (
            <MobileTable tableData={managerUpdatesRows} openManagerUpdateDetailsPage={openManagerUpdateDetailsPage} />
          )}
        </>
      ) : (
        <Table
          sort={{ key, asc }}
          tableData={managerUpdatesRows}
          loading={listInvestmentsLoading}
          forceRecalculateTableFiltersHeight={filterBar.length}
          setSort={onChangeSort}
          openManagerUpdateDetailsPage={openManagerUpdateDetailsPage}
        />
      )}
      <PaginationWrap>
        <TablePagination
          savePagination={onChangePaginationData}
          paginationValues={{
            limit: Number(limit),
            offset: Number(offset),
            total: listInvestmentsData?.listInvestmentsForManagerUpdate?.total ?? 0
          }}
          refetch={() => null as any}
        />
      </PaginationWrap>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
